/**
 * Core Web Vitals Monitoring & Optimization
 * 
 * This module provides:
 * - Web Vitals tracking and reporting
 * - Performance budgets
 * - Optimization hints
 * - Real User Monitoring (RUM) integration
 */

import { useState, useEffect } from 'react';

// ============================================================================
// WEB VITALS THRESHOLDS (Google's recommendations)
// ============================================================================

export const WEB_VITALS_THRESHOLDS = {
  // Largest Contentful Paint - should be under 2.5s
  LCP: {
    good: 2500,
    needsImprovement: 4000,
    unit: 'ms',
    name: 'Largest Contentful Paint',
    description: 'Measures loading performance. Should occur within 2.5 seconds.'
  },
  // First Input Delay - should be under 100ms
  FID: {
    good: 100,
    needsImprovement: 300,
    unit: 'ms',
    name: 'First Input Delay',
    description: 'Measures interactivity. Should be less than 100 milliseconds.'
  },
  // Cumulative Layout Shift - should be under 0.1
  CLS: {
    good: 0.1,
    needsImprovement: 0.25,
    unit: '',
    name: 'Cumulative Layout Shift',
    description: 'Measures visual stability. Should be less than 0.1.'
  },
  // First Contentful Paint - should be under 1.8s
  FCP: {
    good: 1800,
    needsImprovement: 3000,
    unit: 'ms',
    name: 'First Contentful Paint',
    description: 'Measures time to first content. Should be under 1.8 seconds.'
  },
  // Time to First Byte - should be under 800ms
  TTFB: {
    good: 800,
    needsImprovement: 1800,
    unit: 'ms',
    name: 'Time to First Byte',
    description: 'Measures server response time. Should be under 800 milliseconds.'
  },
  // Interaction to Next Paint (replacing FID in 2024)
  INP: {
    good: 200,
    needsImprovement: 500,
    unit: 'ms',
    name: 'Interaction to Next Paint',
    description: 'Measures responsiveness. Should be under 200 milliseconds.'
  }
};

// ============================================================================
// VITALS RATING UTILITY
// ============================================================================

/**
 * Get the rating for a Web Vital metric
 * @param {string} name - Metric name (LCP, FID, CLS, FCP, TTFB, INP)
 * @param {number} value - Metric value
 * @returns {'good' | 'needs-improvement' | 'poor'}
 */
export function getVitalRating(name, value) {
  const threshold = WEB_VITALS_THRESHOLDS[name];
  if (!threshold) return 'unknown';
  
  if (value <= threshold.good) return 'good';
  if (value <= threshold.needsImprovement) return 'needs-improvement';
  return 'poor';
}

/**
 * Format a Web Vital value for display
 */
export function formatVitalValue(name, value) {
  const threshold = WEB_VITALS_THRESHOLDS[name];
  if (!threshold) return String(value);
  
  if (threshold.unit === 'ms') {
    if (value >= 1000) {
      return `${(value / 1000).toFixed(2)}s`;
    }
    return `${Math.round(value)}ms`;
  }
  
  // CLS has no unit
  return value.toFixed(3);
}

// ============================================================================
// WEB VITALS COLLECTOR
// ============================================================================

class WebVitalsCollector {
  constructor() {
    this.metrics = new Map();
    this.listeners = new Set();
    this.initialized = false;
  }

  /**
   * Initialize Web Vitals collection
   */
  async init() {
    if (this.initialized) return;
    
    try {
      // Dynamic import web-vitals library
      const webVitals = await import('web-vitals');
      
      const handleMetric = (metric) => {
        const { name, value, rating, id, delta, entries } = metric;
        
        this.metrics.set(name, {
          name,
          value,
          rating,
          id,
          delta,
          timestamp: Date.now(),
          entries
        });
        
        // Notify listeners
        this.listeners.forEach(listener => {
          try {
            listener(metric);
          } catch (e) {
            console.error('Web Vitals listener error:', e);
          }
        });
        
        // Log to console in development
        if (process.env.NODE_ENV === 'development') {
          const ratingEmoji = rating === 'good' ? '✅' : rating === 'needs-improvement' ? '⚠️' : '❌';
          console.log(`${ratingEmoji} ${name}: ${formatVitalValue(name, value)} (${rating})`);
        }
      };
      
      // Collect all Core Web Vitals (web-vitals v4: FID removed, replaced by INP)
      webVitals.onCLS(handleMetric);
      webVitals.onLCP(handleMetric);
      webVitals.onFCP(handleMetric);
      webVitals.onTTFB(handleMetric);
      webVitals.onINP(handleMetric);
      
      this.initialized = true;
    } catch (error) {
      console.warn('Failed to initialize Web Vitals:', error);
    }
  }

  /**
   * Subscribe to metric updates
   */
  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Get all collected metrics
   */
  getMetrics() {
    return Object.fromEntries(this.metrics);
  }

  /**
   * Get a specific metric
   */
  getMetric(name) {
    return this.metrics.get(name);
  }

  /**
   * Get overall performance score (0-100)
   */
  getPerformanceScore() {
    const weights = {
      LCP: 25,
      FID: 25,
      CLS: 25,
      FCP: 15,
      TTFB: 10
    };
    
    let totalWeight = 0;
    let weightedScore = 0;
    
    for (const [name, weight] of Object.entries(weights)) {
      const metric = this.metrics.get(name);
      if (metric) {
        const score = metric.rating === 'good' ? 100 : 
                      metric.rating === 'needs-improvement' ? 50 : 0;
        weightedScore += score * weight;
        totalWeight += weight;
      }
    }
    
    return totalWeight > 0 ? Math.round(weightedScore / totalWeight) : null;
  }
}

// Singleton instance
export const webVitals = new WebVitalsCollector();

// ============================================================================
// PERFORMANCE BUDGET MONITORING
// ============================================================================

export const PERFORMANCE_BUDGETS = {
  // JavaScript bundle size (compressed)
  jsBundle: {
    warning: 200 * 1024,  // 200KB
    error: 500 * 1024,    // 500KB
    unit: 'bytes'
  },
  // CSS bundle size (compressed)
  cssBundle: {
    warning: 50 * 1024,   // 50KB
    error: 100 * 1024,    // 100KB
    unit: 'bytes'
  },
  // Total page weight
  totalPageWeight: {
    warning: 1.5 * 1024 * 1024,  // 1.5MB
    error: 3 * 1024 * 1024,       // 3MB
    unit: 'bytes'
  },
  // Number of requests
  requestCount: {
    warning: 50,
    error: 100,
    unit: 'requests'
  },
  // Image size (individual)
  imageSize: {
    warning: 100 * 1024,  // 100KB
    error: 500 * 1024,    // 500KB
    unit: 'bytes'
  }
};

/**
 * Check resource timing against budgets
 */
export function checkPerformanceBudgets() {
  if (typeof window === 'undefined' || !window.performance) return null;
  
  const resources = performance.getEntriesByType('resource');
  // navigation timing available for future use: performance.getEntriesByType('navigation')[0]
  
  const results = {
    violations: [],
    warnings: [],
    passed: [],
    metrics: {}
  };
  
  // Calculate totals by type
  const totals = {
    js: 0,
    css: 0,
    image: 0,
    font: 0,
    other: 0,
    total: 0,
    count: resources.length
  };
  
  resources.forEach(resource => {
    const size = resource.transferSize || 0;
    totals.total += size;
    
    if (resource.initiatorType === 'script') {
      totals.js += size;
    } else if (resource.initiatorType === 'css' || resource.name.endsWith('.css')) {
      totals.css += size;
    } else if (resource.initiatorType === 'img' || /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(resource.name)) {
      totals.image += size;
      
      // Check individual image size
      if (size > PERFORMANCE_BUDGETS.imageSize.error) {
        results.violations.push({
          type: 'imageSize',
          resource: resource.name,
          value: size,
          budget: PERFORMANCE_BUDGETS.imageSize.error
        });
      } else if (size > PERFORMANCE_BUDGETS.imageSize.warning) {
        results.warnings.push({
          type: 'imageSize',
          resource: resource.name,
          value: size,
          budget: PERFORMANCE_BUDGETS.imageSize.warning
        });
      }
    } else if (resource.initiatorType === 'font' || /\.(woff|woff2|ttf|otf)$/i.test(resource.name)) {
      totals.font += size;
    } else {
      totals.other += size;
    }
  });
  
  results.metrics = totals;
  
  // Check JS budget
  if (totals.js > PERFORMANCE_BUDGETS.jsBundle.error) {
    results.violations.push({ type: 'jsBundle', value: totals.js, budget: PERFORMANCE_BUDGETS.jsBundle.error });
  } else if (totals.js > PERFORMANCE_BUDGETS.jsBundle.warning) {
    results.warnings.push({ type: 'jsBundle', value: totals.js, budget: PERFORMANCE_BUDGETS.jsBundle.warning });
  } else {
    results.passed.push({ type: 'jsBundle', value: totals.js });
  }
  
  // Check CSS budget
  if (totals.css > PERFORMANCE_BUDGETS.cssBundle.error) {
    results.violations.push({ type: 'cssBundle', value: totals.css, budget: PERFORMANCE_BUDGETS.cssBundle.error });
  } else if (totals.css > PERFORMANCE_BUDGETS.cssBundle.warning) {
    results.warnings.push({ type: 'cssBundle', value: totals.css, budget: PERFORMANCE_BUDGETS.cssBundle.warning });
  } else {
    results.passed.push({ type: 'cssBundle', value: totals.css });
  }
  
  // Check total page weight
  if (totals.total > PERFORMANCE_BUDGETS.totalPageWeight.error) {
    results.violations.push({ type: 'totalPageWeight', value: totals.total, budget: PERFORMANCE_BUDGETS.totalPageWeight.error });
  } else if (totals.total > PERFORMANCE_BUDGETS.totalPageWeight.warning) {
    results.warnings.push({ type: 'totalPageWeight', value: totals.total, budget: PERFORMANCE_BUDGETS.totalPageWeight.warning });
  } else {
    results.passed.push({ type: 'totalPageWeight', value: totals.total });
  }
  
  // Check request count
  if (totals.count > PERFORMANCE_BUDGETS.requestCount.error) {
    results.violations.push({ type: 'requestCount', value: totals.count, budget: PERFORMANCE_BUDGETS.requestCount.error });
  } else if (totals.count > PERFORMANCE_BUDGETS.requestCount.warning) {
    results.warnings.push({ type: 'requestCount', value: totals.count, budget: PERFORMANCE_BUDGETS.requestCount.warning });
  } else {
    results.passed.push({ type: 'requestCount', value: totals.count });
  }
  
  return results;
}

// ============================================================================
// RESOURCE HINTS UTILITIES
// ============================================================================

/**
 * Add preconnect hints for critical origins
 */
export function addPreconnect(origins) {
  if (typeof document === 'undefined') return;
  
  origins.forEach(origin => {
    // Check if already exists
    if (document.querySelector(`link[rel="preconnect"][href="${origin}"]`)) return;
    
    const link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = origin;
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
  });
}

/**
 * Add prefetch hints for likely next navigations
 */
export function addPrefetch(urls) {
  if (typeof document === 'undefined') return;
  
  // Use requestIdleCallback if available
  const schedule = window.requestIdleCallback || ((fn) => setTimeout(fn, 1));
  
  schedule(() => {
    urls.forEach(url => {
      // Check if already exists
      if (document.querySelector(`link[rel="prefetch"][href="${url}"]`)) return;
      
      const link = document.createElement('link');
      link.rel = 'prefetch';
      link.href = url;
      document.head.appendChild(link);
    });
  });
}

/**
 * Preload critical resources
 */
export function addPreload(resources) {
  if (typeof document === 'undefined') return;
  
  resources.forEach(({ href, as, type, crossOrigin }) => {
    // Check if already exists
    if (document.querySelector(`link[rel="preload"][href="${href}"]`)) return;
    
    const link = document.createElement('link');
    link.rel = 'preload';
    link.href = href;
    link.as = as;
    if (type) link.type = type;
    if (crossOrigin) link.crossOrigin = crossOrigin;
    document.head.appendChild(link);
  });
}

// ============================================================================
// OPTIMIZATION RECOMMENDATIONS
// ============================================================================

/**
 * Get optimization recommendations based on collected metrics
 */
export function getOptimizationRecommendations() {
  const metrics = webVitals.getMetrics();
  const budgetResults = checkPerformanceBudgets();
  const recommendations = [];
  
  // LCP recommendations
  if (metrics.LCP && getVitalRating('LCP', metrics.LCP.value) !== 'good') {
    recommendations.push({
      metric: 'LCP',
      priority: 'high',
      title: 'Improve Largest Contentful Paint',
      suggestions: [
        'Optimize and compress images (use WebP format)',
        'Implement lazy loading for below-fold images',
        'Use a CDN for static assets',
        'Preload critical resources',
        'Minimize render-blocking resources'
      ]
    });
  }
  
  // CLS recommendations
  if (metrics.CLS && getVitalRating('CLS', metrics.CLS.value) !== 'good') {
    recommendations.push({
      metric: 'CLS',
      priority: 'high',
      title: 'Reduce Cumulative Layout Shift',
      suggestions: [
        'Set explicit width/height on images and videos',
        'Reserve space for dynamic content',
        'Avoid inserting content above existing content',
        'Use CSS transform for animations instead of layout properties',
        'Preload fonts to avoid FOUT/FOIT'
      ]
    });
  }
  
  // FID/INP recommendations
  const interactionMetric = metrics.INP || metrics.FID;
  if (interactionMetric && getVitalRating(interactionMetric.name || 'FID', interactionMetric.value) !== 'good') {
    recommendations.push({
      metric: interactionMetric.name || 'FID',
      priority: 'high',
      title: 'Improve Input Responsiveness',
      suggestions: [
        'Break up long JavaScript tasks',
        'Use web workers for heavy computations',
        'Defer non-critical JavaScript',
        'Minimize main thread work',
        'Use requestIdleCallback for non-urgent work'
      ]
    });
  }
  
  // TTFB recommendations
  if (metrics.TTFB && getVitalRating('TTFB', metrics.TTFB.value) !== 'good') {
    recommendations.push({
      metric: 'TTFB',
      priority: 'medium',
      title: 'Reduce Server Response Time',
      suggestions: [
        'Use a CDN to serve content closer to users',
        'Optimize server-side code and database queries',
        'Implement server-side caching',
        'Use HTTP/2 or HTTP/3',
        'Consider edge computing/serverless functions'
      ]
    });
  }
  
  // Budget violation recommendations
  if (budgetResults) {
    if (budgetResults.violations.some(v => v.type === 'jsBundle')) {
      recommendations.push({
        metric: 'Bundle Size',
        priority: 'medium',
        title: 'Reduce JavaScript Bundle Size',
        suggestions: [
          'Implement code splitting with React.lazy()',
          'Remove unused dependencies',
          'Use tree shaking effectively',
          'Consider lighter alternatives to heavy libraries',
          'Analyze bundle with webpack-bundle-analyzer'
        ]
      });
    }
    
    if (budgetResults.violations.some(v => v.type === 'imageSize')) {
      recommendations.push({
        metric: 'Image Size',
        priority: 'medium',
        title: 'Optimize Large Images',
        suggestions: [
          'Compress images before upload',
          'Use responsive images with srcset',
          'Convert to WebP format',
          'Implement lazy loading',
          'Use appropriate image dimensions'
        ]
      });
    }
  }
  
  return recommendations;
}

// ============================================================================
// PERFORMANCE MONITORING HOOK
// ============================================================================

/**
 * React hook for monitoring Web Vitals
 */
export function useWebVitals() {
  const [metrics, setMetrics] = useState({});
  const [score, setScore] = useState(null);
  
  useEffect(() => {
    // Initialize collector
    webVitals.init();
    
    // Subscribe to updates
    const unsubscribe = webVitals.subscribe((metric) => {
      setMetrics(prev => ({
        ...prev,
        [metric.name]: metric
      }));
      setScore(webVitals.getPerformanceScore());
    });
    
    return unsubscribe;
  }, []);
  
  return {
    metrics,
    score,
    getRecommendations: getOptimizationRecommendations
  };
}

// ============================================================================
// CRITICAL ORIGINS FOR THIS APP
// ============================================================================

export const CRITICAL_ORIGINS = [
  'https://fonts.googleapis.com',
  'https://fonts.gstatic.com',
  'https://firebasestorage.googleapis.com',
  'https://www.gstatic.com'
];

/**
 * Initialize performance optimizations
 * Call this early in app lifecycle
 */
export function initPerformanceOptimizations() {
  // Add preconnects for critical origins
  addPreconnect(CRITICAL_ORIGINS);
  
  // Initialize Web Vitals collection
  webVitals.init();
  
  // Log performance budget check in development
  if (process.env.NODE_ENV === 'development') {
    window.addEventListener('load', () => {
      setTimeout(() => {
        const results = checkPerformanceBudgets();
        if (results) {
          if (results.violations.length > 0) {
            console.warn('⚠️ Performance budget violations:', results.violations);
          }
          if (results.warnings.length > 0) {
            console.warn('⚡ Performance budget warnings:', results.warnings);
          }
          console.log('📊 Resource metrics:', results.metrics);
        }
      }, 3000); // Wait for page to fully load
    });
  }
}

const webVitalsUtils = {
  WEB_VITALS_THRESHOLDS,
  PERFORMANCE_BUDGETS,
  CRITICAL_ORIGINS,
  webVitals,
  getVitalRating,
  formatVitalValue,
  checkPerformanceBudgets,
  addPreconnect,
  addPrefetch,
  addPreload,
  getOptimizationRecommendations,
  useWebVitals,
  initPerformanceOptimizations
};

export default webVitalsUtils;
